// ===== Menu bar: File Edit View Insert Charts Tools Window Help =====
import { useState, useEffect } from "react";
import { useStore, setUI, setChart, chartOf, newChart, closeChart, addIndicator, update, TF_LIST } from "./store";
import { MenuList } from "./ContextMenu";
import { zoomChart } from "./Chart";

export default function MenuBar() {
  const st = useStore();
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const h = () => setOpen(null);
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);
  const c = chartOf();
  const togglePanel = (k) => setUI({ panels: { ...st.panels, [k]: !st.panels[k] } });
  const order = () => setUI({ dialog: { kind: "order", symbol: c.symbol, mode: "market" } });
  const ind = (t, l) => ({ label: l, onClick: () => addIndicator(t) });
  const tool = (t, l) => ({ label: l, checked: st.tool === t, onClick: () => setUI({ tool: t }) });

  const menus = {
    File: [
      { label: "New Chart", items: st.watch.slice(0, 16).map((s) => ({ label: s, onClick: () => newChart(s) })) },
      { label: "Open Deleted", disabled: true },
      { label: "Profiles", items: [{ label: "Default", checked: true }] },
      { label: "Close", shortcut: "Ctrl+F4", onClick: () => closeChart(st.activeChart) },
      { sep: true },
      { label: "Save As Picture", onClick: () => document.querySelector(`[data-testid=chart-${st.activeChart}] canvas`)?.toBlob((b) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "chart.png"; a.click(); }) },
      { label: "Print...", shortcut: "Ctrl+P", onClick: () => window.print() },
      { sep: true },
      { label: "Open an Account", disabled: true },
      { label: "Login to Trade Account", onClick: () => setUI({ dialog: { kind: "about" } }) },
    ],
    Edit: [
      { label: "Undo Delete", shortcut: "Ctrl+Z", onClick: () => setChart({ drawings: c.drawings.slice(0, -1) }) },
      { label: "Delete All Objects", onClick: () => setChart({ drawings: [] }) },
      { sep: true },
      { label: "Clear Journal", onClick: () => update((s) => { s.journal = []; }) },
    ],
    View: [
      { label: "Languages", items: [{ label: "English", checked: true }] },
      { label: "Toolbars", items: [{ label: "Standard", checked: true }, { label: "Charts", checked: true }, { label: "Line Studies", checked: true }, { label: "Timeframes", checked: true }] },
      { label: "Status Bar", checked: true },
      { sep: true },
      { label: "Market Watch", shortcut: "Ctrl+M", checked: st.panels.market, onClick: () => togglePanel("market") },
      { label: "Navigator", shortcut: "Ctrl+N", checked: st.panels.navigator, onClick: () => togglePanel("navigator") },
      { label: "Toolbox", shortcut: "Ctrl+T", checked: st.panels.toolbox, onClick: () => togglePanel("toolbox") },
      { sep: true },
      { label: "Dark Theme", checked: st.theme === "dark", onClick: () => update((s) => { s.theme = "dark"; }) },
      { label: "Light Theme", checked: st.theme === "light", onClick: () => update((s) => { s.theme = "light"; }) },
      { sep: true },
      { label: "Full Screen", shortcut: "F11", onClick: () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()) },
    ],
    Insert: [
      { label: "Indicators", items: [
        { label: "Trend", items: [ind("SMA", "Moving Average"), ind("EMA", "Exponential Moving Average"), ind("BB", "Bollinger Bands")] },
        { label: "Oscillators", items: [ind("RSI", "Relative Strength Index"), ind("MACD", "MACD"), ind("STOCH", "Stochastic Oscillator")] },
      ] },
      { label: "Objects", items: [
        { label: "Lines", items: [tool("vline", "Vertical Line"), tool("hline", "Horizontal Line"), tool("trend", "Trendline")] },
        { label: "Fibonacci", items: [tool("fib", "Fibonacci Retracement")] },
        { label: "Shapes", items: [tool("rect", "Rectangle")] },
      ] },
    ],
    Charts: [
      { label: "Depth Of Market", shortcut: "Alt+B", onClick: () => setUI({ dom: c.symbol }) },
      { label: "Indicators List", shortcut: "Ctrl+I", onClick: () => setUI({ dialog: { kind: "indicators", chartId: c.id } }) },
      { label: "Objects", items: [{ label: "Delete All", onClick: () => setChart({ drawings: [] }) }] },
      { sep: true },
      { label: "Bar Chart", shortcut: "Alt+1", checked: c.type === "bars", onClick: () => setChart({ type: "bars" }) },
      { label: "Candlesticks", shortcut: "Alt+2", checked: c.type === "candles", onClick: () => setChart({ type: "candles" }) },
      { label: "Line Chart", shortcut: "Alt+3", checked: c.type === "line", onClick: () => setChart({ type: "line" }) },
      { label: "Timeframes", items: TF_LIST.map((tf) => ({ label: tf, checked: c.tf === tf, onClick: () => setChart({ tf }) })) },
      { label: "Symbol", items: st.watch.map((s) => ({ label: s, checked: c.symbol === s, onClick: () => setChart({ symbol: s }) })) },
      { sep: true },
      { label: "Auto Scroll", checked: c.autoScroll, onClick: () => setChart({ autoScroll: !c.autoScroll }) },
      { label: "Grid", shortcut: "Ctrl+G", checked: c.grid, onClick: () => setChart({ grid: !c.grid }) },
      { label: "Volumes", shortcut: "Ctrl+K", checked: c.volumes, onClick: () => setChart({ volumes: !c.volumes }) },
      { label: "Zoom In", shortcut: "+", onClick: () => zoomChart(c.id, 1.3) },
      { label: "Zoom Out", shortcut: "-", onClick: () => zoomChart(c.id, 1 / 1.3) },
      { sep: true },
      { label: "Properties...", shortcut: "F8", onClick: () => setUI({ dialog: { kind: "properties", chartId: c.id } }) },
    ],
    Tools: [
      { label: "New Order", shortcut: "F9", onClick: order },
      { label: "One Click Trading", shortcut: "Alt+T", checked: c.oneClick, onClick: () => setChart({ oneClick: !c.oneClick }) },
      { label: "Live Crypto Feed (Binance)", checked: st.liveFeed, onClick: () => update((s) => { s.liveFeed = !s.liveFeed; }) },
      { label: "Price Alert...", onClick: () => setUI({ dialog: { kind: "alert", symbol: c.symbol } }) },
      { label: "Indicator Alert...", onClick: () => setUI({ dialog: { kind: "alert", alertKind: "rsi", symbol: c.symbol, tf: c.tf } }) },
      { label: "Algo Trading", shortcut: "Ctrl+E", checked: st.algo, onClick: () => update((s) => { s.algo = !s.algo; }) },
      { sep: true },
      { label: "Options", shortcut: "Ctrl+O", onClick: () => setUI({ dialog: { kind: "properties", chartId: c.id } }) },
    ],
    Window: [
      { label: "New Window", items: st.watch.slice(0, 12).map((s) => ({ label: s, onClick: () => newChart(s) })) },
      { sep: true },
      { label: "Tile Windows", shortcut: "Alt+R", checked: st.tile, onClick: () => update((s) => { s.tile = true; }) },
      { label: "Tabbed Windows", checked: !st.tile, onClick: () => update((s) => { s.tile = false; }) },
      { sep: true },
      ...st.charts.map((ch, i) => ({ label: `${i + 1} ${ch.symbol},${ch.tf}`, checked: ch.id === st.activeChart, onClick: () => setUI({ activeChart: ch.id }) })),
    ],
    Help: [
      { label: "Help Topics", shortcut: "F1", onClick: () => setUI({ dialog: { kind: "about" } }) },
      { sep: true },
      { label: "About", onClick: () => setUI({ dialog: { kind: "about" } }) },
    ],
  };

  return (
    <div className="mt-menubar" data-testid="menu-bar">
      {Object.keys(menus).map((m) => (
        <div
          key={m}
          className={`mt-menubar-item ${open === m ? "active" : ""}`}
          data-testid={`menubar-${m.toLowerCase()}`}
          onMouseDown={(e) => { e.stopPropagation(); setOpen(open === m ? null : m); }}
          onMouseEnter={() => open && setOpen(m)}
        >
          <u>{m[0]}</u>{m.slice(1)}
          {open === m && <MenuList items={menus[m]} style={{ position: "absolute", top: 21, left: 0, zIndex: 900 }} onDone={() => setOpen(null)} testId={`menu-${m.toLowerCase()}`} />}
        </div>
      ))}
    </div>
  );
}
